import React from "react";
import { IntlProvider } from "react-intl";
import { gql, useApolloClient } from "@apollo/client";
import { useForm, Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { TextField, SelectField } from "@syos-music/form";
import {
  Instrument,
  InstrumentType,
  InstrumentSelectField,
  InstrumentTypeSelectField,
  MaterialColorSelectField,
  MouthpieceTipOpeningSelectField,
  MaterialColor,
} from "@syos-music/instruments";
import { Product } from "@syos-music/products";

import {
  styled,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Fab,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Delete as RemoveIcon,
  Plus as AddIcon,
  Printer as PrintIcon,
  Download as ImportIcon,
} from "mdi-material-ui";

import Layout from "components/common/Layout";
import Barcode from "components/product/Barcode";
import SignatureMouthpieceSelectField from "components/signatureMouthpiece/SignatureMouthpieceSelectField";

const intlMessages = {
  ...require("@syos-music/form/lang/en.json"),
  ...require("@syos-music/instruments/lang/en.json"),
  ...require("@syos-music/products/lang/en.json"),
};

const OriginalsType = Product.displayModes.originalsMouthpiece.OriginalsType;
const OriginalsTypeSelectField =
  Product.displayModes.originalsMouthpiece.OriginalsTypeSelectField;
const OriginalsTipOpeningSelectField =
  Product.displayModes.originalsMouthpiece.OriginalsTipOpeningSelectField;

function SkuField({ name }) {
  const form = useForm();

  const type = form.getFieldState(`${name}.type`)?.value;
  const instrument = form.getFieldState(`${name}.instrument`)?.value;
  const instrumentType = form.getFieldState(`${name}.instrumentType`)?.value;
  const originalsType = form.getFieldState(`${name}.originalsType`)?.value;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <SelectField
          name={`${name}.type`}
          label="Type"
          required
          onChange={(e) => {
            form.change(e.target.name, e.target.value);
            form.change(`${name}.originalsType`, null);
            form.change(`${name}.signatureMouthpiece`, null);
            form.change(`${name}.tipOpening`, null);
          }}
        >
          <MenuItem value="originals">Originals</MenuItem>
          <MenuItem value="signature">Signature</MenuItem>
        </SelectField>
      </Grid>

      <Grid item xs={12} md={4}>
        <InstrumentSelectField
          name={`${name}.instrument`}
          required
          onChange={(e) => {
            form.change(e.target.name, e.target.value);
            form.change(`${name}.instrumentType`, null);
            form.change(`${name}.signatureMouthpiece`, null);
            form.change(`${name}.tipOpening`, null);
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        {instrument && (
          <InstrumentTypeSelectField
            instrument={instrument}
            name={`${name}.instrumentType`}
            required
            onChange={(e) => {
              form.change(e.target.name, e.target.value);
              form.change(`${name}.signatureMouthpiece`, null);
              form.change(`${name}.tipOpening`, null);
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        {type === "originals" && (
          <OriginalsTypeSelectField name={`${name}.originalsType`} required />
        )}

        {type === "signature" && (
          <SignatureMouthpieceSelectField
            name={`${name}.signatureMouthpiece`}
            required
            filters={{
              instrument: instrument,
              instrumentType: instrumentType,
            }}
          />
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        {type === "originals" && (
          <OriginalsTipOpeningSelectField
            name={`${name}.tipOpening`}
            instrument={instrument}
            instrumentType={instrumentType}
            originalsType={originalsType}
          />
        )}

        {type === "signature" && (
          <MouthpieceTipOpeningSelectField
            name={`${name}.tipOpening`}
            instrument={instrument}
            instrumentType={instrumentType}
          />
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <MaterialColorSelectField name={`${name}.mouthpieceColor`} />
      </Grid>
    </Grid>
  );
}

const LabelRoot = styled(Stack)(({ theme }) => ({
  margin: "2mm",
  width: "32mm",
  height: "85mm",
  padding: theme.spacing(1),
  border: "0.75mm solid currentColor",
  "@media print": { color: "black" },
}));

const LabelLine = styled(Typography, {
  shouldForwardProp: (p) => !["uppercase", "bold"].includes(p),
})(({ theme, uppercase, bold }) => ({
  flexShrink: 0,
  fontSize: "12px",
  textTransform: uppercase && "uppercase",
  fontWeight: bold && 700,
}));

const Separator = styled("div")(({ theme }) => ({
  border: "none",
  borderBottom: "0.5mm solid currentColor",
  margin: 0,
  height: 0,
}));

function Label({ label }) {
  if (!label)
    return (
      <LabelRoot spacing={1}>
        <LabelLine bold>No GTIN match</LabelLine>
      </LabelRoot>
    );

  const { gtin, sku, signatureMouthpiece } = label;

  const [type, instrument, instrumentType, spec, tipOpening, mouthpieceColor] =
    sku.split("|");

  return (
    <LabelRoot spacing={1}>
      <LabelLine uppercase bold>
        Syos
        <br />
        {type}
      </LabelLine>

      <Separator />

      <LabelLine noWrap>
        <InstrumentType
          instrument={instrument}
          instrumentType={instrumentType}
        />
        &nbsp;
        <Instrument instrument={instrument} />
      </LabelLine>

      <Separator />

      <LabelLine noWrap>
        {type === "originals" && <OriginalsType originalsType={spec} />}
        {type === "signature" && signatureMouthpiece?.signature}
      </LabelLine>

      <Separator />

      <LabelLine noWrap>
        <MaterialColor color={mouthpieceColor} variant="text" />
      </LabelLine>

      <Separator />

      <LabelLine sx={{ flexGrow: 1 }}>
        <span style={{ fontSize: "small" }}>Tip opening</span>
        <span style={{ fontSize: "xxx-large", float: "right" }}>
          {tipOpening}
        </span>
      </LabelLine>

      <Barcode ean13={gtin} />
    </LabelRoot>
  );
}

export default function BarcodesPage() {
  const client = useApolloClient();

  const [initialValues, setInitialValues] = React.useState({ rows: [{}] });

  const handleImport = React.useCallback(
    async ({ orderId }) => {
      const { data } = await client.query({
        query: gql`
          query ($orderId: ID!) {
            order(id: $orderId) {
              id

              cart {
                id
                sku
                quantity
              }
            }
          }
        `,
        variables: { orderId },
      });

      const rows = data.order.cart.reduce((acc, { sku, quantity }) => {
        if (sku.split("|").length < 6)
          return acc.concat(Array(quantity).fill({}, 0));

        const [
          type,
          instrument,
          instrumentType,
          spec,
          tipOpening,
          mouthpieceColor,
        ] = sku.split("|");

        const row = {
          type,
          instrument,
          instrumentType,
          originalsType: type === "originals" ? spec : null,
          signatureMouthpiece: type === "signature" ? spec : null,
          tipOpening,
          mouthpieceColor,
        };

        return acc.concat(Array(quantity).fill(row, 0));
      }, []);

      setInitialValues({ rows });
    },
    [client]
  );

  const [labels, setLabels] = React.useState([]);

  const handleSubmit = React.useCallback(
    async (values) => {
      const skus = values.rows.map((row) =>
        [
          row.type,
          row.instrument,
          row.instrumentType,
          row.type === "originals" && row.originalsType,
          row.type === "signature" && row.signatureMouthpiece,
          row.tipOpening,
          row.mouthpieceColor,
        ]
          .filter(Boolean)
          .join("|")
      );

      const { data } = await client.query({
        query: gql`
          query ($skus: [String]!) {
            mapSkuGtins(skus: $skus) {
              id
              sku
              gtin
              signatureMouthpiece {
                id
                signature
              }
            }
          }
        `,
        variables: { skus },
      });

      setLabels(data.mapSkuGtins);
    },
    [client]
  );

  const printRef = React.useRef();
  const handlePrint = React.useCallback(() => {
    const printHtml = printRef.current.innerHTML;

    const iframe = document.createElement("iframe");
    iframe.width = 0;
    iframe.height = 0;
    iframe.style.position = "fixed";
    iframe.style.top = "0px";
    iframe.style.left = "0px";

    document.body.appendChild(iframe);

    for (const node of document.querySelectorAll("style")) {
      const cloned = node.cloneNode(true);
      iframe.contentDocument.head.appendChild(cloned);

      for (const rule of node.sheet.cssRules) {
        cloned.sheet.insertRule(rule.cssText);
      }
    }

    iframe.contentDocument.head.append(
      ...Array.from(document.querySelectorAll("link[rel=stylesheet]"), (node) =>
        node.cloneNode(true)
      )
    );

    iframe.contentDocument.body.innerHTML = printHtml;

    iframe.focus();
    iframe.contentWindow.print();

    document.body.removeChild(iframe);
  }, []);

  return (
    <Container fixed sx={{ p: 2, position: "relative" }}>
      <Layout.Toolbar fixed>
        <Form onSubmit={handleImport}>
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <Stack
                spacing={2}
                direction="row"
                alignItems="center"
                // justifyContent="flex-end"
              >
                <Tooltip title="Identifiant de la commande">
                  <div>
                    <TextField
                      name="orderId"
                      placeholder="Importer depuis une commande"
                      fullWidth={false}
                      sx={{ minWidth: 300 }}
                    />
                  </div>
                </Tooltip>

                <Button type="submit" size="small" startIcon={<ImportIcon />}>
                  Importer
                </Button>
              </Stack>
            </form>
          )}
        </Form>
      </Layout.Toolbar>

      <Form
        mutators={{ ...arrayMutators }}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      >
        {({ form, handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit} noValidate autoComplete="off">
            <FieldArray name="rows">
              {({ fields }) => (
                <Stack spacing={2}>
                  {fields.map((fieldName, idx) => (
                    <Stack
                      key={fieldName}
                      spacing={2}
                      direction="row"
                      alignItems="center"
                      sx={{
                        pb: 2,
                        borderBottom: (t) => `1px solid ${t.palette.divider}`,
                      }}
                    >
                      <SkuField name={fieldName} />

                      <IconButton onClick={() => fields.remove(idx)}>
                        <RemoveIcon />
                      </IconButton>
                    </Stack>
                  ))}

                  <Stack spacing={1} direction="row" justifyContent="right">
                    <Button
                      onClick={() => fields.push({})}
                      startIcon={<AddIcon />}
                      variant="text"
                      sx={{ alignSelf: "flex-end" }}
                    >
                      Ajouter une ligne
                    </Button>

                    <LoadingButton type="submit" loading={submitting}>
                      Générer
                    </LoadingButton>
                  </Stack>
                </Stack>
              )}
            </FieldArray>
          </form>
        )}
      </Form>

      <Divider sx={{ m: (t) => t.spacing(2, 0) }} />

      {!!labels?.length && (
        <Fab
          onClick={handlePrint}
          sx={{
            position: "absolute",
            right: (t) => t.spacing(3),
            "&:not(:hover)": { opacity: 0.5 },
            tansition: (t) =>
              t.transitions.create(["background-color", "opacity"]),
          }}
        >
          <PrintIcon />
        </Fab>
      )}

      <IntlProvider locale="en" messages={intlMessages}>
        <Box
          sx={{ "@media print": { width: "21cm", height: "29.7cm" } }}
          ref={printRef}
        >
          <Grid container spacing={0}>
            {labels.map((label, idx) => (
              <Grid key={`${idx}-${label?.id}`} item>
                <Label label={label} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </IntlProvider>
    </Container>
  );
}
